import { useTranslation } from 'react-i18next'
import { AlertTriangle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { useHasPermission } from '@/lib/hooks/usePermission'

interface CloseCrisisDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CloseCrisisDialog({ open, onOpenChange }: CloseCrisisDialogProps) {
  const { t } = useTranslation()
  const { closeCrisis, isLoading, activeCrisis, clearActiveCrisis } = useCrisisStore()
  const canCloseCrisis = useHasPermission('crisis.close')

  const handleCloseCrisis = async () => {
    if (!activeCrisis) return

    try {
      await closeCrisis(activeCrisis.id)
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to close crisis:', error)
    }
  }

  const handleLeaveCrisis = () => {
    // Clear user's selected crisis but keep it active on the server
    clearActiveCrisis()
    onOpenChange(false)
    if (typeof window !== 'undefined') {
      window.location.href = '/crisis/select'
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-99 p-4">
      <Card className="w-full max-w-md bg-white">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            {t('crisis.closeCrisis')}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <p className="text-sm text-muted-foreground mb-6">{t('crisis.closeCrisisConfirm')}</p>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleLeaveCrisis}
              disabled={isLoading}
            >
              {t('crisis.leaveCrisis')}
            </Button>

            {canCloseCrisis && (
              <Button
                type="button"
                variant="gradient"
                onClick={handleCloseCrisis}
                disabled={isLoading}
              >
                {isLoading ? t('common.closing') : t('crisis.closeCrisis')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
